import { useState } from "react";
import { allGenres, regions, years } from "@/mocks/movies";
import type { SortKey } from "@/utils/movies";

type FilterBarProps = {
  region: string;
  genre: string;
  year: string;
  sort: SortKey;
  resultCount: number;
  onChange: (patch: Partial<{ region: string; genre: string; year: string; sort: SortKey }>) => void;
  onReset: () => void;
};

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "updated", label: "最近更新" },
  { key: "views", label: "按热度" },
  { key: "rating", label: "按评分" },
];

export default function FilterBar({
  region,
  genre,
  year,
  sort,
  resultCount,
  onChange,
  onReset,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const hasFilter = region !== "全部" || genre !== "全部" || year !== "全部";

  const Pill = ({
    active,
    label,
    onClick,
  }: {
    active: boolean;
    label: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
        active
          ? "bg-primary-500 text-background-50"
          : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-list-check-2 text-primary-600" />
          共 <strong className="text-foreground-950">{resultCount}</strong> 部影片
        </div>
        <div className="flex items-center gap-2">
          {hasFilter && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className="ri-equalizer-line" />
            筛选条件
          </button>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} lg:block`}>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="w-12 flex-none text-sm text-foreground-500">地区</span>
          <Pill active={region === "全部"} label="全部" onClick={() => onChange({ region: "全部" })} />
          {regions.map((r) => (
            <Pill key={r} active={region === r} label={r} onClick={() => onChange({ region: r })} />
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="w-12 flex-none text-sm text-foreground-500">类型</span>
          <Pill active={genre === "全部"} label="全部" onClick={() => onChange({ genre: "全部" })} />
          {allGenres.map((g) => (
            <Pill key={g} active={genre === g} label={g} onClick={() => onChange({ genre: g })} />
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">年份</span>
            <select
              value={year}
              onChange={(e) => onChange({ year: e.target.value })}
              className="h-9 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              <option value="全部">全部年份</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y} 年
                </option>
              ))}
              <option value="2015前">2015 年以前</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-500">排序</span>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => onChange({ sort: opt.key })}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    sort === opt.key
                      ? "bg-background-50 text-primary-700 shadow-sm"
                      : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}